import {
  AddPrescriptionCartItem,
  CarePlanCartItem,
  CheckoutOption,
  UpdateCarePlanCartItem
} from '@/features/subscription/types/create-subscription.type';
import { getFastestShippingMethod } from '@/features/subscription/utils/subscription-cart.util';
import { InvoicePaymentStatusEnum } from '@/models/invoice.model';
import { SubscriptionFull } from '@/models/subscription.model';
import { RootState } from '@/store';
import { Interval } from '@/types/common.type';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

type State = typeof initialState;

const initialState = {
  step: 1,
  cartItems: [],
  isScheduled: true,
  startsAt: null,
  checkoutOption: 'COLLECT_PAYMENT',
  isTransactionInProgress: false,
  checkoutSessionUrl: null,
  createdSubscriptions: []
} as {
  step: number;
  cartItems: CarePlanCartItem[];
  isScheduled: boolean;
  startsAt: Date | null;
  checkoutOption: CheckoutOption;
  isTransactionInProgress: boolean;
  checkoutSessionUrl: string | null;
  createdSubscriptions: SubscriptionFull[];
};

const slice = createSlice({
  name: 'createSubscriptions',
  initialState,
  reducers: {
    updateState: (state, action: PayloadAction<Partial<State>>) => {
      Object.assign(state, action.payload);
    },
    resetState: (state, action: PayloadAction<Partial<State> | undefined>) => {
      Object.assign(state, initialState, action.payload);
    },
    updateCartItem: (state, action: PayloadAction<UpdateCarePlanCartItem>) => {
      const { _id, ...updates } = action.payload;
      const carePlanIndex = state.cartItems.findIndex(
        (item) => item._id === _id
      );

      if (carePlanIndex === -1) return;

      state.cartItems[carePlanIndex] = {
        ...state.cartItems[carePlanIndex],
        ...updates
      };
    },
    addPrescriptionCartItem: (
      state,
      action: PayloadAction<AddPrescriptionCartItem>
    ) => {
      const { carePlan, billingInterval, ...newItem } = action.payload;
      const newItemId = newItem.product.medication.id;

      const carePlanIndex = state.cartItems.findIndex(
        (item) => item._id === carePlan.id
      );

      // No care plans in cart, create the first one
      if (carePlanIndex === -1) {
        state.cartItems.push({
          _id: carePlan.id,
          carePlan: carePlan,
          items: [
            {
              _id: newItemId,
              ...newItem
            }
          ],
          shippingMethod: newItem.defaultShippingMethod,
          billingInterval: billingInterval,
          coupon: null
        });
        return;
      }

      const existingCarePlan = state.cartItems[carePlanIndex];
      // Update shipping method to the fastest one between existing and new item
      state.cartItems[carePlanIndex].shippingMethod = getFastestShippingMethod([
        existingCarePlan.shippingMethod,
        newItem.defaultShippingMethod
      ]);

      // Check if the item already exists in the care plan and replace it
      const itemIndex = state.cartItems[carePlanIndex].items.findIndex(
        (item) => item._id === newItemId
      );

      // Replace existing item
      if (itemIndex !== -1) {
        state.cartItems[carePlanIndex].items[itemIndex] = {
          _id: newItemId,
          ...newItem
        };
        return;
      }

      // Add new item to existing care plan
      state.cartItems[carePlanIndex].items.push({
        _id: newItemId,
        ...newItem
      });
    },
    removePrescriptionCartItem: (
      state,
      action: PayloadAction<{ carePlanId: string; medicationId: string }>
    ) => {
      const { carePlanId, medicationId } = action.payload;

      const carePlanIndex = state.cartItems.findIndex(
        (item) => item._id === carePlanId
      );

      if (carePlanIndex === -1) return;

      const itemIndex = state.cartItems[carePlanIndex].items.findIndex(
        (item) => item._id === medicationId
      );

      if (itemIndex === -1) return;

      state.cartItems[carePlanIndex].items.splice(itemIndex, 1);

      // If no items left in care plan, remove the care plan from cart
      if (state.cartItems[carePlanIndex].items.length === 0) {
        state.cartItems.splice(carePlanIndex, 1);
      }
    },
    updateCreateSubscription: (
      state,
      action: PayloadAction<SubscriptionFull>
    ) => {
      const updatedSubscription = action.payload;
      const index = state.createdSubscriptions.findIndex(
        (sub) => sub.id === updatedSubscription.id
      );

      if (index !== -1) {
        state.createdSubscriptions[index] = updatedSubscription;
      }
    }
  }
});

export const {
  updateState,
  resetState,
  updateCartItem,
  addPrescriptionCartItem,
  removePrescriptionCartItem,
  updateCreateSubscription
} = slice.actions;

export default slice.reducer;

export const selectStep = (state: RootState): State['step'] =>
  state.createSubscription.step;

export const selectCartItems = (state: RootState): State['cartItems'] =>
  state.createSubscription.cartItems;

export const selectIsScheduled = (state: RootState): State['isScheduled'] =>
  state.createSubscription.isScheduled;

export const selectStartsAt = (state: RootState): State['startsAt'] =>
  state.createSubscription.startsAt;

export const selectCheckoutOption = (
  state: RootState
): State['checkoutOption'] => state.createSubscription.checkoutOption;

export const selectIsTransactionInProgress = (
  state: RootState
): State['isTransactionInProgress'] =>
  state.createSubscription.isTransactionInProgress;

export const selectCheckoutSessionUrl = (
  state: RootState
): State['checkoutSessionUrl'] => {
  return state.createSubscription.checkoutSessionUrl;
};

export const selectCreatedSubscriptions = (
  state: RootState
): State['createdSubscriptions'] => {
  return state.createSubscription.createdSubscriptions;
};

export const selectIntervalPerCarePlan = createSelector(
  (state: RootState) => state.createSubscription.cartItems,
  (cartItems): { [carePlanId: string]: Interval } => {
    const intervalPerCarePlan: { [carePlanId: string]: Interval } = {};

    cartItems.forEach((carePlanCartItem) => {
      intervalPerCarePlan[carePlanCartItem._id] =
        carePlanCartItem.billingInterval;
    });

    return intervalPerCarePlan;
  }
);

export const selectAllMedicationIdsInCart = createSelector(
  (state: RootState) => state.createSubscription.cartItems,
  (cartItems): string[] => {
    return cartItems
      .map((cartItem) => cartItem.items.map((item) => item._id))
      .flat();
  }
);

export const selectCreatedSubscriptionsWithFailedPayments = createSelector(
  (state: RootState) => state.createSubscription.createdSubscriptions,
  (subscriptions): State['createdSubscriptions'] => {
    return subscriptions.filter(
      (subscription) =>
        subscription.latestInvoice?.paymentStatus ===
        InvoicePaymentStatusEnum.FAILED
    );
  }
);

export const selectCreatedSubscriptionsWithSuccessPayments = createSelector(
  (state: RootState) => state.createSubscription.createdSubscriptions,
  (subscriptions): State['createdSubscriptions'] => {
    return subscriptions.filter(
      (subscription) =>
        !subscription.latestInvoice ||
        subscription.latestInvoice.paymentStatus !==
          InvoicePaymentStatusEnum.FAILED
    );
  }
);
